import { useState } from 'react'
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  CircularProgress,
} from '@mui/material'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import PrintIcon from '@mui/icons-material/Print'
import DownloadIcon from '@mui/icons-material/Download'
import AddIcon from '@mui/icons-material/Add'
import SendIcon from '@mui/icons-material/Send'
import { toast } from 'react-toastify'
import { sendCertificateToPatient } from '../medicalCertificateService'

/**
 * Step 3 of the create flow: shows the authoritative server-generated PDF (via its signed URL) with
 * Print / Download / Send to patient, mirroring EkaCare's success screen. Sending queues delivery
 * (dispatched by the ~15-min batch); the dialog offers an immediate "Send now" that bypasses the queue.
 */
export default function CertificateResult({ certificate, onCreateAnother, onClose }) {
  const url = certificate?.pdfUrl
  const certId = certificate?.id
  const [dialogOpen, setDialogOpen] = useState(false)
  const [sending, setSending] = useState(false)
  // null -> not sent; 'queued' -> queued for ~15 min; 'sent' -> sent immediately
  const [sendState, setSendState] = useState(null)

  const handlePrint = () => {
    if (!url) return
    const win = window.open(url, '_blank')
    if (win) {
      win.addEventListener('load', () => {
        try {
          win.focus()
          win.print()
        } catch (e) {
          /* user can print from the opened tab */
        }
      })
    }
  }

  const handleDownload = () => {
    if (url) window.open(url, '_blank', 'noopener')
  }

  // Clicking "Send to patient" queues delivery, then opens a dialog informing the ~15-min ETA
  // and offering an immediate send.
  const handleSend = async () => {
    if (!certId) return
    setSending(true)
    const res = await sendCertificateToPatient(certId, false)
    setSending(false)
    if (res.ok) {
      setSendState('queued')
      setDialogOpen(true)
    } else {
      toast.error(res.message || 'Failed to queue certificate')
    }
  }

  const handleSendNow = async () => {
    if (!certId) return
    setSending(true)
    const res = await sendCertificateToPatient(certId, true)
    setSending(false)
    if (res.ok) {
      setSendState('sent')
      toast.success('Certificate sent to the patient')
    } else {
      toast.error(res.message || 'Failed to send now')
    }
  }

  return (
    <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap', alignItems: 'flex-start' }}>
      <Box sx={{ flex: '0 0 250px', display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <CheckCircleIcon sx={{ color: 'success.main' }} />
          <Typography sx={{ fontWeight: 600 }}>Document saved</Typography>
        </Box>
        <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>{certificate?.certificateTitle}</Typography>

        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button variant="outlined" startIcon={<PrintIcon />} onClick={handlePrint} disabled={!url} sx={{ textTransform: 'none' }}>
            Print
          </Button>
          <Button variant="outlined" startIcon={<DownloadIcon />} onClick={handleDownload} disabled={!url} sx={{ textTransform: 'none' }}>
            Download
          </Button>
        </Box>

        <Button
          variant="contained"
          color="secondary"
          startIcon={sending ? <CircularProgress size={16} color="inherit" /> : <SendIcon />}
          onClick={handleSend}
          disabled={sending || !certId || sendState === 'sent'}
          sx={{ textTransform: 'none' }}
        >
          {sendState === 'sent' ? 'Sent to patient' : 'Send to patient'}
        </Button>

        <Button startIcon={<AddIcon />} onClick={onCreateAnother} sx={{ textTransform: 'none', justifyContent: 'flex-start' }}>
          Create another
        </Button>
        <Button variant="contained" onClick={onClose} sx={{ textTransform: 'none' }}>
          Done
        </Button>
      </Box>

      <Box sx={{ flex: 1, minWidth: 320 }}>
        {url ? (
          <iframe
            title="Certificate PDF"
            src={url}
            style={{ width: '100%', height: '65vh', border: '1px solid #e0e0e0', borderRadius: 8 }}
          />
        ) : (
          <Typography color="text.secondary">
            The certificate was saved but the preview could not be loaded. Use Download to open it.
          </Typography>
        )}
      </Box>

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>{sendState === 'sent' ? 'Sent to patient' : 'Queued for the patient'}</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ fontSize: 14 }}>
            {sendState === 'sent'
              ? 'The certificate has been sent to the patient on their registered contact (email / WhatsApp).'
              : 'The certificate will be sent to the patient on their registered contact (email / WhatsApp) — usually within ~15 minutes during the day, or ~40 minutes at night. You can send it right away instead.'}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          {sendState !== 'sent' && (
            <Button
              onClick={handleSendNow}
              disabled={sending}
              startIcon={sending ? <CircularProgress size={16} color="inherit" /> : <SendIcon />}
              sx={{ textTransform: 'none' }}
            >
              Send now
            </Button>
          )}
          <Button variant="contained" onClick={() => setDialogOpen(false)} sx={{ textTransform: 'none' }}>
            {sendState === 'sent' ? 'Done' : 'OK'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}
